import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../utils/apiUtils";
import { clearCorporateSession, getCorporateToken } from "../utils/corporateSession";

const CorporateStaff = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const [showAddStaff, setShowAddStaff] = useState(false);

  const [showStaffBookings, setShowStaffBookings] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffBookingsLoading, setStaffBookingsLoading] = useState(false);
  const [staffBookingsData, setStaffBookingsData] = useState(null);

  const [staffForm, setStaffForm] = useState({
    email: "",
    fullName: "",
    mobileNumber: "",
    designation: "",
  });

  const fetchMe = async () => {
    try {
      const res = await api.get("/corporate/me");
      if (res?.data?.success) {
        setMe(res.data.data);
      } else {
        toast.error(res?.data?.message || "Failed to load profile");
        clearCorporateSession();
        navigate("/corporate/login", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load profile");
      clearCorporateSession();
      navigate("/corporate/login", { replace: true });
    }
  };

  const fetchStaff = async () => {
    setStaffLoading(true);
    try {
      const res = await api.get("/corporate/staff");
      if (res?.data?.success) {
        setStaff(res.data.data || []);
      } else {
        toast.error(res?.data?.message || "Failed to load staff");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load staff");
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    const token = getCorporateToken();
    if (!token) {
      navigate("/corporate/login", { replace: true });
      return;
    }

    (async () => {
      setLoading(true);
      await fetchMe();
      setLoading(false);
    })();
  }, [navigate]);

  useEffect(() => {
    if (me?.role === "admin") {
      fetchStaff();
    }
  }, [me?.role]);

  const onStaffChange = (e) => {
    setStaffForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  const addStaff = async (e) => {
    e.preventDefault();

    if (!staffForm.email || !staffForm.fullName || !staffForm.mobileNumber) {
      toast.error("Email, full name and mobile number are required");
      return;
    }

    if (!/^[0-9]{10}$/.test(String(staffForm.mobileNumber || '').trim())) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setStaffLoading(true);
    try {
      const res = await api.post("/corporate/staff", {
        email: staffForm.email,
        fullName: staffForm.fullName,
        mobileNumber: staffForm.mobileNumber,
        designation: staffForm.designation,
      });

      if (res?.data?.success) {
        toast.success(res.data.message || "Staff created successfully");
        setStaffForm({ email: "", fullName: "", mobileNumber: "", designation: "" });
        setShowAddStaff(false);
        await fetchStaff();
      } else {
        toast.error(res?.data?.message || "Failed to create staff");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to create staff");
    } finally {
      setStaffLoading(false);
    }
  };

  const deactivateStaff = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this staff member?")) {
      return;
    }
    setStaffLoading(true);
    try {
      const res = await api.put(`/corporate/staff/${id}/deactivate`, {});
      if (res?.data?.success) {
        toast.success(res.data.message || "Staff deactivated successfully");
        await fetchStaff();
      } else {
        toast.error(res?.data?.message || "Failed to deactivate staff");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to deactivate staff");
    } finally {
      setStaffLoading(false);
    }
  };

  const openStaffBookings = async (staffMember) => {
    setSelectedStaff(staffMember);
    setShowStaffBookings(true);
    setStaffBookingsLoading(true);
    setStaffBookingsData(null);

    try {
      const res = await api.get(`/corporate/staff/${staffMember._id}/bookings`);
      if (res?.data?.success) {
        setStaffBookingsData(res.data.data);
      } else {
        toast.error(res?.data?.message || "Failed to load staff bookings");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load staff bookings");
    } finally {
      setStaffBookingsLoading(false);
    }
  };

  const closeStaffBookings = () => {
    setShowStaffBookings(false);
    setSelectedStaff(null);
    setStaffBookingsData(null);
    setStaffBookingsLoading(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 py-12">
        <div className="w-12 h-12 border-4 border-[#F56753] border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-sm font-medium text-slate-500">Loading details...</p>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="max-w-md mx-auto my-12 text-center p-8 bg-white border border-slate-100 shadow-sm rounded-2xl">
        <div className="w-16 h-16 mx-auto bg-amber-50 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-800">Session Expired</h2>
        <p className="text-sm text-slate-500 mt-2">Please login to your corporate account again.</p>
        <button
          onClick={() => navigate("/corporate/login")}
          className="mt-6 w-full py-2.5 px-4 bg-[#F56753] hover:bg-[#d94e3a] text-white rounded-xl font-medium shadow-sm transition-all"
        >
          Login Now
        </button>
      </div>
    );
  }

  if (me.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto my-12 p-8 bg-white border border-slate-100 shadow-sm rounded-2xl">
        <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mb-5">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Not Authorized</h2>
        <p className="text-sm text-slate-500 mt-2">Only a corporate administrator has the privilege to manage staff and access control list details for {me?.companyName || "your company"}.</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-white border border-slate-100 rounded-2xl shadow-sm">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#F56753] bg-red-50 px-3 py-1 rounded-full w-max mb-2">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
            {me?.companyName || "Corporate Account"}
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">Staff Management</h1>
          <p className="text-sm text-slate-500 mt-1">Add, manage, and view travel booking history of your team members.</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddStaff(true)}
            disabled={staffLoading}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#F56753] to-[#d94e3a] hover:from-[#6b1818] hover:to-[#4a0f0f] text-white rounded-xl font-medium shadow-sm shadow-red-100 hover:shadow-md transition-all duration-200 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Add Member
          </button>
          
          <button
            onClick={fetchStaff}
            disabled={staffLoading}
            className="flex items-center justify-center p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-100 text-slate-600 hover:text-slate-800 rounded-xl transition-all duration-200 disabled:opacity-50"
            title="Refresh Staff List"
          >
            <svg className={`w-5 h-5 ${staffLoading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3 3 3"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Main Staff Container */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 bg-[#F56753] rounded-full animate-ping"></span>
            <h2 className="text-base font-bold text-slate-800">Team Directory</h2>
          </div>
          <span className="text-xs font-semibold text-[#F56753] bg-red-50 px-3 py-1 rounded-full">
            {staff.length} {staff.length === 1 ? "Member" : "Members"}
          </span>
        </div>

        {staffLoading && staff.length === 0 ? (
          <div className="p-8 space-y-4">
            <div className="h-8 bg-slate-100 rounded-lg animate-pulse w-1/4"></div>
            <div className="space-y-2">
              <div className="h-10 bg-slate-50 rounded-lg animate-pulse"></div>
              <div className="h-10 bg-slate-50 rounded-lg animate-pulse"></div>
              <div className="h-10 bg-slate-50 rounded-lg animate-pulse"></div>
            </div>
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 mx-auto bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
            <h3 className="text-base font-bold text-slate-800">No staff members found</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">Get started by adding employees so they can request and book rides under your corporate business program.</p>
            <button
              onClick={() => setShowAddStaff(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-[#F56753] rounded-xl text-sm font-semibold transition-all duration-200"
            >
              Add Your First Member
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/30">
                  <th className="px-6 py-4">Employee</th>
                  <th className="px-6 py-4">Contact Info</th>
                  <th className="px-6 py-4">Role/Designation</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {staff.map((s) => (
                  <tr key={s._id} className="hover:bg-slate-50/40 transition-colors group">
                    <td className="px-6 py-4.5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-100 to-slate-200 flex items-center justify-center font-bold text-slate-600 border border-white shadow-sm overflow-hidden uppercase">
                          {s.profileImageUrl ? (
                            <img src={s.profileImageUrl} alt={s.fullName || "User"} className="w-full h-full object-cover" />
                          ) : (
                            s.fullName ? s.fullName.charAt(0) : "U"
                          )}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-800 tracking-tight">{s.fullName || "Unnamed User"}</h4>
                          <span className="text-xs text-[#F56753] font-medium">Emp ID: {s._id?.substring(18) || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4.5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-sm text-slate-700">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                          </svg>
                          <span>{s.email}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                          </svg>
                          <span>{s.mobileNumber || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4.5">
                      <span className="text-sm font-semibold text-slate-700 bg-slate-100/70 px-2.5 py-1 rounded-lg">
                        {s.designation || "Executive"}
                      </span>
                    </td>
                    <td className="px-6 py-4.5">
                      {s.isActive ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          disabled={staffLoading}
                          onClick={() => openStaffBookings(s)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-red-50 text-slate-700 hover:text-[#F56753] rounded-lg text-xs font-bold transition-all border border-slate-200 hover:border-red-100"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                          </svg>
                          Bookings
                        </button>
                        {s.isActive && (
                          <button
                            type="button"
                            disabled={staffLoading}
                            onClick={() => deactivateStaff(s._id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-xs font-bold transition-all border border-rose-100"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                            </svg>
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Staff Bookings Modal */}
      {showStaffBookings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl border border-slate-100 flex flex-col max-h-[85vh] overflow-hidden animate-in fade-in-50 zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 text-[#F56753] rounded-xl flex items-center justify-center">
                  <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Ride History</h3>
                  <p className="text-xs font-semibold text-[#F56753] mt-0.5">
                    {selectedStaff?.fullName} ({selectedStaff?.designation || "Executive"})
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeStaffBookings}
                disabled={staffBookingsLoading}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {staffBookingsLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-4 border-[#F56753] border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm font-medium text-slate-500 mt-3 animate-pulse">Fetching bookings data...</p>
                </div>
              ) : !staffBookingsData ? (
                <div className="text-center py-10 text-slate-500">
                  No billing history details returned.
                </div>
              ) : (
                <>
                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-slate-50 to-red-50/20 border border-slate-100 rounded-xl p-5 relative overflow-hidden">
                      <div className="absolute right-3 top-3 opacity-10 text-[#F56753]">
                        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Total Bookings</span>
                      <span className="text-2xl font-black text-slate-900 mt-1 block">{staffBookingsData.totalBookings || 0}</span>
                    </div>

                    <div className="bg-gradient-to-br from-slate-50 to-emerald-50/20 border border-slate-100 rounded-xl p-5 relative overflow-hidden">
                      <div className="absolute right-3 top-3 opacity-10 text-emerald-600">
                        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Spend Volume</span>
                      <span className="text-2xl font-black text-emerald-700 mt-1 block">
                        ₹{Number(staffBookingsData.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div className="bg-gradient-to-br from-slate-50 to-red-50/20 border border-slate-100 rounded-xl p-5 relative overflow-hidden">
                      <div className="absolute right-3 top-3 opacity-10 text-[#F56753]">
                        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Retrieved Trips</span>
                      <span className="text-2xl font-black text-[#F56753] mt-1 block">{(staffBookingsData.bookings || []).length}</span>
                    </div>
                  </div>

                  {/* Modal Bookings List */}
                  {(staffBookingsData.bookings || []).length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl bg-slate-50/30">
                      <p className="text-sm font-medium text-slate-500">This member has not booked any rides yet.</p>
                    </div>
                  ) : (
                    <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="overflow-x-auto max-h-80">
                        <table className="w-full text-left border-collapse text-sm">
                          <thead>
                            <tr className="border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-50">
                              <th className="px-4 py-3">Pickup Info</th>
                              <th className="px-4 py-3">Route details</th>
                              <th className="px-4 py-3">Cab Type</th>
                              <th className="px-4 py-3">Trip Status</th>
                              <th className="px-4 py-3 text-right">Fare</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {staffBookingsData.bookings.map((b) => {
                              // Custom Badge mapping
                              let badgeColor = "bg-slate-100 text-slate-700 border-slate-200";
                              if (b.status === "completed") badgeColor = "bg-emerald-50 text-emerald-700 border-emerald-100";
                              else if (["assigned", "inProgress"].includes(b.status)) badgeColor = "bg-red-50 text-[#F56753] border-red-100";
                              else if (["cancelled", "rejected"].includes(b.status)) badgeColor = "bg-rose-50 text-rose-700 border-rose-100";

                              return (
                                <tr key={b._id} className="hover:bg-slate-50/40 transition-colors">
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="font-semibold text-slate-800">
                                      {b.pickupDate ? new Date(b.pickupDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "-"}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{b.pickupTime || ""}</div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="space-y-1 max-w-[280px]">
                                      <div className="flex items-start gap-1">
                                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-1.5 shrink-0"></span>
                                        <span className="text-xs font-semibold text-slate-700 truncate" title={b.pickupLocation?.name || b.pickupAddress}>
                                          {b.pickupLocation?.name || b.pickupAddress || "-"}
                                        </span>
                                      </div>
                                      <div className="flex items-start gap-1">
                                        <span className="w-1.5 h-1.5 bg-rose-500 rounded-full mt-1.5 shrink-0"></span>
                                        <span className="text-xs text-slate-500 truncate" title={b.dropLocation?.name || b.dropAddress}>
                                          {b.dropLocation?.name || b.dropAddress || "-"}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 font-medium text-slate-700 whitespace-nowrap">
                                    {b.cabType?.name || "Standard Cab"}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`inline-flex px-2 py-0.5 border text-xs font-semibold rounded-full capitalize ${badgeColor}`}>
                                      {b.status || "-"}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 font-bold text-slate-800 text-right whitespace-nowrap">
                                    ₹{Number(b.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
              <button
                type="button"
                onClick={closeStaffBookings}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-100 text-slate-700 text-sm font-semibold rounded-xl transition-all"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Staff Dialog Modal */}
      {showAddStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in-50 zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 text-[#F56753] rounded-xl flex items-center justify-center">
                  <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Add Staff Member</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Invite employee to utilize the corporate ride portal.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddStaff(false)}
                disabled={staffLoading}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={addStaff}>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Full Name *</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                        </svg>
                      </span>
                      <input
                        name="fullName"
                        value={staffForm.fullName}
                        onChange={onStaffChange}
                        placeholder="John Doe"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#F56753] focus:ring-4 focus:ring-red-100/30 transition-all duration-200"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Email Address *</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                      </span>
                      <input
                        name="email"
                        value={staffForm.email}
                        onChange={onStaffChange}
                        placeholder="john.doe@company.com"
                        type="email"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#F56753] focus:ring-4 focus:ring-red-100/30 transition-all duration-200"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Mobile Number (10 digit) *</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                        </svg>
                      </span>
                      <input
                        name="mobileNumber"
                        value={staffForm.mobileNumber}
                        onChange={onStaffChange}
                        placeholder="9876543210"
                        inputMode="numeric"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#F56753] focus:ring-4 focus:ring-red-100/30 transition-all duration-200"
                        required
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Job Designation</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                      </span>
                      <input
                        name="designation"
                        value={staffForm.designation}
                        onChange={onStaffChange}
                        placeholder="Product Manager / Engineer"
                        className="w-full bg-slate-50/50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-[#F56753] focus:ring-4 focus:ring-red-100/30 transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddStaff(false)}
                  disabled={staffLoading}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-100 text-slate-700 text-sm font-semibold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={staffLoading}
                  className="flex items-center gap-2 px-5 py-2 bg-[#F56753] hover:bg-[#d94e3a] text-white rounded-xl text-sm font-semibold shadow-sm shadow-red-100 transition-all duration-200 disabled:opacity-50"
                >
                  {staffLoading ? "Creating..." : "Save Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateStaff;
